/**
 * Browser Print / Save as PDF helpers.
 *
 * Download PDF clones on-screen editor chrome (`.print-visible`), which is a
 * viewport-height `absolute inset-0 overflow-auto` scroller. Chrome then
 * paginates only the first sheet and can stack overflow on that page (mashed
 * overlapping words). Flatten those locks and print the prose, not the scroller.
 */

export const PRINT_CONTAINER_ID = 'print-container'

export type PrintSource =
  | { kind: 'html-fragment'; html: string }
  | { kind: 'element'; element: HTMLElement }
  | { kind: 'plain-text'; text: string }

const VIEWPORT_LOCK_CLASS_RE =
  /\b(absolute|fixed|inset-0|overflow-auto|overflow-hidden|overflow-scroll|overflow-y-auto|overflow-y-hidden|overflow-x-hidden|h-full|h-screen|min-h-0|min-h-full|max-h-full|max-h-screen)\b/

const VIEWPORT_LOCK_CLASSES = [
  'absolute',
  'fixed',
  'inset-0',
  'overflow-auto',
  'overflow-hidden',
  'overflow-scroll',
  'overflow-y-auto',
  'overflow-y-hidden',
  'overflow-x-hidden',
  'h-full',
  'h-screen',
  'min-h-0',
  'min-h-full',
  'max-h-full',
  'max-h-screen',
] as const

const PRINT_CHROME_SELECTOR = [
  '.print-hide',
  '[data-testid="markdown-preview-reading-gutter"]',
  '[data-testid="markdown-preview-reading-gutter-chat"]',
].join(', ')

function classNameOf(el: HTMLElement): string {
  if (typeof el.className === 'string') return el.className
  return el.getAttribute('class') || ''
}

function isViewportHeight(value: string): boolean {
  return value === '100%' || value === '100vh' || value === '100dvh'
}

export function looksLikeViewportLock(el: HTMLElement): boolean {
  if (VIEWPORT_LOCK_CLASS_RE.test(classNameOf(el))) return true
  if (isViewportHeight(el.style.height) || isViewportHeight(el.style.maxHeight)) return true
  const overflow = el.style.overflow || el.style.overflowY
  return overflow === 'hidden' || overflow === 'auto' || overflow === 'scroll'
}

export function flattenViewportLocks(root: HTMLElement): void {
  const walk = (node: HTMLElement) => {
    if (node === root || looksLikeViewportLock(node)) {
      for (const name of VIEWPORT_LOCK_CLASSES) {
        node.classList.remove(name)
      }
      node.style.position = 'static'
      node.style.inset = 'auto'
      node.style.top = 'auto'
      node.style.right = 'auto'
      node.style.bottom = 'auto'
      node.style.left = 'auto'
      node.style.height = 'auto'
      node.style.maxHeight = 'none'
      node.style.minHeight = '0'
      node.style.overflow = 'visible'
      node.style.overflowX = 'visible'
      node.style.overflowY = 'visible'
    }
    for (const child of Array.from(node.children)) {
      if (child instanceof HTMLElement) walk(child)
    }
  }
  walk(root)
}

export function stripPrintChrome(root: HTMLElement): void {
  root.querySelectorAll(PRINT_CHROME_SELECTOR).forEach((el) => el.remove())
}

/** Pull body + styles from a full HTML document so we do not nest <html> in a div. */
export function fragmentFromHtmlDocument(htmlSource: string): string {
  const parsed = new DOMParser().parseFromString(htmlSource, 'text/html')
  const headBits = Array.from(parsed.head.querySelectorAll('style, link[rel="stylesheet"]'))
    .map((node) => node.outerHTML)
    .join('')
  return `${headBits}${parsed.body.innerHTML}`
}

export function resolvePrintSource(root: ParentNode = document): PrintSource | null {
  const iframe = root.querySelector('[data-html-document-preview] iframe') as HTMLIFrameElement | null
  const srcdoc = iframe?.srcdoc || iframe?.getAttribute('srcdoc') || ''
  if (srcdoc.trim()) {
    return { kind: 'html-fragment', html: fragmentFromHtmlDocument(srcdoc) }
  }

  const prose = root.querySelector('.print-visible .document-reading-prose') as HTMLElement | null
  if (prose) return { kind: 'element', element: prose }

  const prosemirror = root.querySelector('.print-visible .ProseMirror') as HTMLElement | null
  if (prosemirror) return { kind: 'element', element: prosemirror }

  const cmContent = root.querySelector('.print-visible .cm-content') as HTMLElement | null
  const cmText = (cmContent?.innerText || cmContent?.textContent || '').trim()
  if (cmText) return { kind: 'plain-text', text: cmText }

  const fallback = root.querySelector('.print-visible') as HTMLElement | null
  if (fallback) return { kind: 'element', element: fallback }

  return null
}

export function fillPrintContainer(container: HTMLElement, source: PrintSource): void {
  container.replaceChildren()

  if (source.kind === 'html-fragment') {
    container.innerHTML = source.html
    flattenViewportLocks(container)
    return
  }

  if (source.kind === 'plain-text') {
    const pre = document.createElement('pre')
    pre.style.whiteSpace = 'pre-wrap'
    pre.style.overflowWrap = 'break-word'
    pre.textContent = source.text
    container.appendChild(pre)
    return
  }

  const clone = source.element.cloneNode(true) as HTMLElement
  clone.style.color = '#000'
  clone.style.background = 'white'
  clone.style.maxWidth = '100%'
  stripPrintChrome(clone)
  flattenViewportLocks(clone)
  container.appendChild(clone)
}

export function createPrintContainer(): HTMLDivElement {
  const container = document.createElement('div')
  container.id = PRINT_CONTAINER_ID
  // Do not lock height to the viewport. Print CSS also forces height:auto.
  container.style.cssText = `
    position: static;
    width: 100%;
    height: auto;
    overflow: visible;
    background: white;
    color: #000;
    z-index: 99999;
    padding: 0.5in;
  `
  return container
}

export function mountPrintContainer(source: PrintSource): HTMLDivElement {
  const container = createPrintContainer()
  fillPrintContainer(container, source)
  document.body.appendChild(container)
  return container
}
